import { Route, Routes } from '@angular/router';

import { PatientGroupsComponent } from '@features/patient-groups/patient-groups.component';

import { ROUTING_PATH } from '@shared/constants/routingPath';
import { CanDeactivateGuard } from '@shared/guards/can-deactivate.guard';

import { InvestigatorPatientGroupsGuard } from './guards/investigator-patient-groups.guard';
import { InvestigatorDetailGuard } from './investigator-detail/guards/investigator-detail.guard';
import { InvestigatorDetailComponent } from './investigator-detail/investigator-detail.component';
import { InvestigatorForecastPageComponent } from './investigator-forecast/investigator-forecast-page.component';
import { InvestigatorForecastGuard } from './investigator-forecast/tabs/investigator-forecast-table/guards/investigator-forecast.guard';
import { InvestigatorForecastComponent } from './investigator-forecast/tabs/investigator-forecast-table/investigator-forecast.component';
import { InvestigatorSettingsGuard } from './investigator-forecast/tabs/investigator-settings/guards/investigator-settings.guard';
import { InvestigatorForecastSettingsComponent } from './investigator-forecast/tabs/investigator-settings/investigator-settings.component';
import { InvestigatorReconciliationDashboardComponent } from './investigator-reconciliation-dashboard/investigator-reconciliation-dashboard.component';
import { InvestigatorSummaryGuard } from './investigator-summary/guards/investigator-summary.guard';
import { InvestigatorSummaryComponent } from './investigator-summary/investigator-summary.component';
import { InvestigatorTransactionsGuard } from './investigator-transactions/guards/investigator-transactions.guard';
import { InvestigatorTransactionsComponent } from './investigator-transactions/investigator-transactions.component';
import { InvestigatorComponent } from './investigator.component';
import { PatientProtocolEditGuard } from './patient-protocol-edit/guards/patient-protocol-edit.guard';
import { PatientProtocolEditComponent } from './patient-protocol-edit/patient-protocol-edit.component';
import { PatientTrackerGuard } from './patient-tracker/guards/patient-tracker.guard';
import { PatientTrackerComponent } from './patient-tracker/patient-tracker.component';
import { ProtocolEntryComponent } from './protocol-entry/protocol-entry.component';
import { PatientProtocolGuard } from './site-costs/patient-protocol/guards/patient-protocol.guard';
import { PatientProtocolComponent } from './site-costs/patient-protocol/patient-protocol.component';
import { SiteBudgetComponent } from './site-costs/site';
import { SiteComponentComponent } from './site-costs/site/site.components';
import { SiteInfoComponent } from './site-costs/site/tabs/site-info/site-info.components';
import { SiteCostsComponent } from './site-costs/site-costs.component';
import { SitesGuard } from './site-costs/sites/guards/sites.guard';
import { SitesComponent } from './site-costs/sites/sites.component';

const INVESTIGATOR_ROUTES: Routes = [
  {
    canActivate: [InvestigatorSummaryGuard],
    component: InvestigatorSummaryComponent,
    path: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_SUMMARY,
  },
  {
    canActivate: [InvestigatorTransactionsGuard],
    canDeactivate: [CanDeactivateGuard],
    component: InvestigatorTransactionsComponent,
    path: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_TRANSACTIONS,
  },
  {
    canActivate: [PatientTrackerGuard],
    component: PatientTrackerComponent,
    path: ROUTING_PATH.INVESTIGATOR.PATIENT_TRACKER,
  },
  {
    children: [
      {
        canActivate: [PatientProtocolEditGuard],
        canDeactivate: [CanDeactivateGuard],
        component: PatientProtocolEditComponent,
        path: '',
      },
      {
        canActivate: [InvestigatorPatientGroupsGuard],
        canDeactivate: [CanDeactivateGuard],
        component: PatientGroupsComponent,
        path: ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.PATIENT_GROUPS,
      },
    ],
    component: ProtocolEntryComponent,
    path: ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX,
  },
  {
    children: [
      {
        canActivate: [SitesGuard],
        component: SitesComponent,
        path: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX,
      },
      {
        canActivate: [SitesGuard],
        canActivateChild: [SitesGuard],
        children: [
          {
            canDeactivate: [CanDeactivateGuard],
            component: SiteInfoComponent,
            path: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS,
          },
          {
            canDeactivate: [CanDeactivateGuard],
            component: SiteBudgetComponent,
            path: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.COSTS,
          },
        ],
        component: SiteComponentComponent,
        path: `${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/:id`,
      },
      {
        canActivate: [PatientProtocolGuard],
        component: PatientProtocolComponent,
        path: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITE_COST_SUMMARY,
      },
      {
        path: '**',
        redirectTo: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX,
      },
    ],
    component: SiteCostsComponent,
    path: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX,
  },
  {
    children: [
      {
        canActivate: [InvestigatorForecastGuard],
        canDeactivate: [CanDeactivateGuard],
        component: InvestigatorForecastComponent,
        path: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.TABLE,
      },
      {
        canActivate: [InvestigatorSettingsGuard],
        component: InvestigatorForecastSettingsComponent,
        path: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.SETTINGS,
      },
      {
        path: '**',
        redirectTo: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.TABLE,
      },
    ],
    component: InvestigatorForecastPageComponent,
    path: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.INDEX,
  },
  {
    canActivate: [InvestigatorDetailGuard],
    component: InvestigatorDetailComponent,
    path: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_DETAIL,
  },
  {
    canActivate: [InvestigatorTransactionsGuard],
    canDeactivate: [CanDeactivateGuard],
    component: InvestigatorReconciliationDashboardComponent,
    path: ROUTING_PATH.INVESTIGATOR.PAYMENTS_RECONCILIATION,
  },
];

export const buildInvestigatorRoutes = (): Route => {
  return {
    children: [
      ...INVESTIGATOR_ROUTES,
      {
        path: '**',
        redirectTo: ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_SUMMARY,
      },
    ],
    component: InvestigatorComponent,
    path: ROUTING_PATH.INVESTIGATOR.INDEX,
  };
};
